import { useCallback, useEffect, useRef, useState } from 'react';
import useGoogleMapsApiLoader from 'src/infrastructure/google/useGoogleMapsApiLoader';
import { generateColorHashFromString } from 'src/common/generateColorHashFromString';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import useDebouncer from 'src/common/useDebouncer';
import { loadRealEstateData, setLocation } from '../onsite-visit.slice';
import { feature, flatten } from '@turf/turf';
import { Building } from '../onsite-visit.dto';
import { Box, CircularProgress } from '@mui/material';

interface MapProps {
    selectedBuildings: Building[] | null;
    onSelectBuildings: (buildings: Building[]) => void;
}

const LOAD_BUILDINGS_DEBOUNCE_MS = 2000;

export default function OnsiteVisitMap({ selectedBuildings, onSelectBuildings }: MapProps) {
    const dispatch = useAppDispatch();
    const loader = useGoogleMapsApiLoader();

    const { location, buildings, isMapLoading, highlightedBuilding } = useAppSelector((state) => state.onsiteVisit);
    const locationRef = useRef(location);

    const [map, setMap] = useState<google.maps.Map | undefined>();
    const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();
    const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement>();
    const [polygons, setPolygons] = useState<{
        [key: string]: google.maps.Polygon[];
    }>({});
    const [localSelectedBuildings, setLocalSelectedBuildings] = useState<Building[]>(selectedBuildings || []);
    const loadBuildingsDebouncer = useDebouncer();

    useEffect(() => {
        if (selectedBuildings) {
            setLocalSelectedBuildings(selectedBuildings);
        }
    }, [selectedBuildings]);

    useEffect(() => {
        if (!map) {
            loadMap();
        }
    }, []);

    useEffect(() => {
        if (map !== undefined && geocoder !== undefined) {
            google.maps.event.addListener(map, 'dragend', () => dragend(map, geocoder));
        }
    }, [map, geocoder]);

    async function dragend(map: google.maps.Map, geocoder: google.maps.Geocoder): Promise<void> {
        if (map !== undefined && geocoder !== undefined) {
            try {
                const center = map.getCenter();
                if (!center) {
                    throw new Error('Map center is not defined');
                }

                const geocoderResponse: google.maps.GeocoderResponse = await geocoder.geocode({
                    location: center,
                });

                dispatch(
                    setLocation({
                        address: geocoderResponse.results[0].formatted_address,
                        lat: center.lat(),
                        lng: center.lng(),
                    }),
                );
            } catch (e) {
                console.error('Geocoder failed due to: ' + e);
            }
        }
    }

    useEffect(() => {
        locationRef.current = location;

        if (!location) {
            return;
        }

        loadBuildingsDebouncer(() => {
            dispatch(
                loadRealEstateData({
                    location,
                    radius: 1000,
                }),
            );
        }, LOAD_BUILDINGS_DEBOUNCE_MS);
    }, [location]);

    useEffect(() => {
        refreshCenter();
        refreshPolygons();
        refreshSearchMarker();
    }, [buildings, location]);

    useEffect(() => {
        refreshPolygons();
    }, [localSelectedBuildings, buildings, highlightedBuilding]);

    function refreshCenter() {
        if (map !== undefined && location) {
            const centerLatLng = new google.maps.LatLng(location.lat, location.lng);
            map.setCenter(centerLatLng);
        }
    }

    async function refreshSearchMarker(): Promise<void> {
        if (map !== undefined && location) {
            const { AdvancedMarkerElement } = (await loader.importLibrary('marker')) as google.maps.MarkerLibrary;
            if (marker !== undefined) {
                marker.map = null;
                setMarker(marker);
            }

            setMarker(
                new AdvancedMarkerElement({
                    position: { lat: location.lat, lng: location.lng },
                    map,
                    title: location.address + '\n' + location.lat + ',' + location.lng,
                }),
            );
        }
    }

    async function loadMap() {
        const mapOptions: google.maps.MapOptions = {
            mapId: 'onsite-map',
            mapTypeId: 'satellite',
            mapTypeControl: false,
            tilt: 0,
            tiltInteractionEnabled: false,
            streetViewControl: false,
            fullscreenControl: false,
        };
        const mapElement = document.getElementById('onsite-map');
        if (mapElement !== null) {
            const { Map } = (await loader.importLibrary('maps')) as google.maps.MapsLibrary;

            // Since this is an async function, the location property might be a stale value
            // We therefore use a ref to access the latest version.
            if (locationRef.current) {
                mapOptions.center = { lat: locationRef.current.lat, lng: locationRef.current.lng };
                mapOptions.zoom = 18;
            }

            setMap(new Map(mapElement, mapOptions));

            const { Geocoder } = (await loader.importLibrary('geocoding')) as google.maps.GeocodingLibrary;
            setGeocoder(new Geocoder());
        }
    }

    async function refreshPolygons() {
        if (!map || !buildings) return;

        // Clear existing polygons
        Object.values(polygons).forEach((polygons) => polygons.forEach((polygon) => polygon.setMap(null)));
        setPolygons({});

        // Initialize New Polygon Map
        const selectedColor = 'f5cb5a';

        // Iterate over all buildings
        buildings.forEach((building) => {
            const isSelected = localSelectedBuildings.some((b) => b.code === building.code);
            const isHighlighted = highlightedBuilding === building.code;
            const unselectedColor = generateColorHashFromString(building.code);

            // Determine the color: selected buildings use selectedColor, others get unique colors
            const color = isSelected ? selectedColor : unselectedColor;

            const buildingsPolygons = flatten(feature(building.geometry));
            buildingsPolygons.features.forEach((feature) => {
                // Extract polygon paths from the feature's coordinates
                const polygonPaths = feature.geometry.coordinates.map((ring: [number, number][]) =>
                    ring.map(([lng, lat]) => ({ lat, lng })),
                );
                // Create a new polygon with the determined color
                const polygon = new google.maps.Polygon({
                    paths: polygonPaths,
                    strokeColor: `#${color}`,
                    strokeOpacity: 1,
                    strokeWeight: 1,
                    fillColor: `#${color}`,
                    fillOpacity: isHighlighted ? 1 : 0.5,
                });

                // Add Click Event to Toggle Selection
                google.maps.event.addListener(polygon, 'click', () => toggleBuildingSelection(building));

                // Display the polygon on the map
                polygon.setMap(map);

                // Update state with the new polygon
                const statePolygons = polygons;
                if (!statePolygons[building.code]) {
                    statePolygons[building.code] = [];
                }
                statePolygons[building.code].push(polygon);

                setPolygons(statePolygons);
            });
        });
    }

    const toggleBuildingSelection = useCallback(
        (building: Building) => {
            const isAlreadySelected = localSelectedBuildings.some((b) => b.code === building.code);
            const updatedSelectedBuildings = isAlreadySelected
                ? localSelectedBuildings.filter((b) => b.code !== building.code)
                : [...localSelectedBuildings, building];

            setLocalSelectedBuildings(updatedSelectedBuildings);

            if (JSON.stringify(localSelectedBuildings) !== JSON.stringify(updatedSelectedBuildings)) {
                onSelectBuildings(updatedSelectedBuildings);
            }
        },
        [localSelectedBuildings, onSelectBuildings],
    );

    return (
        <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
            <div id="onsite-map" style={{ height: '100%', width: '100%' }} />
            {isMapLoading && (
                <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
}
